import cloneDeep from "lodash-es/cloneDeep";
import { unwrapNodes } from "@/helpers/graphql";
import { hasFinalSaleTag } from "@/helpers/finalSale.js";
import { hasUkWarehouseTag } from "@/helpers/ukWarehouse.js";
import { getFeatureToggles } from "@/helpers/featureToggles.js";

function variantIsClassicPreorder(variant, product) {
  if (product?.tags?.includes("hide-preorder")) return false;

  const raw = variant?.randbPreorder?.value;
  if (!raw) return false;

  try {
    const { enabled = false, limit = 0, reserved = 0 } = JSON.parse(raw);
    const inventory = variant.quantityAvailable || 0;
    return enabled && inventory <= 0 && reserved < limit;
  } catch {
    return false;
  }
}

const state = {
  product: {},
  selectedOptions: [],
  selectedVariant: null,
  defaultVariant: null,
  openSizeGuide: false,
  openQuickLook: false,
  /** Product shown in the Shop the Look slide-in — separate from PDP `product`. */
  quickLookProduct: null,
  quickLookSelectedVariant: null,
  openPreorderModal: false,
  preorderModalShown: false,
  openFinalSaleVariantModal: false,
  finalSaleModalShown: false,
  /** Product id that already showed the final-sale popup from quick look. */
  finalSaleQuickLookProductId: null,
  openUkWarehouseVariantModal: false,
  ukWarehouseModalShown: false,
  /** Product id that already showed the UK warehouse popup from quick look. */
  ukWarehouseQuickLookProductId: null,
};

const getters = {
  selectedOptionsWithoutId: (state) => {
    return state.selectedOptions.map(({ name, value }) => ({
      name,
      value,
    }));
  },
  productOptions: (state) => {
    return state.product.options;
  },
  variant: (state) => {
    return state.selectedVariant || state.defaultVariant;
  },
  price: (state) => {
    return state.product.priceRange.minVariantPrice;
  },
  originalPrice: (state) => {
    return state.product.compareAtPriceRange.minVariantPrice;
  },
  isOnSale: (_, getters) => {
    return (
      parseFloat(getters.originalPrice.amount) >
      parseFloat(getters.price.amount)
    );
  },
  salePreviewPrice: (state) => {
    return state.product.salePreviewPrice?.value || null;
  },
  variants: (state) => {
    const variants = unwrapNodes(state.product?.variants?.edges) || [];

    const sizeOnlyVariants = variants.map((variant) => {
      const title = variant.title.split(" / ")[0];

      return {
        ...variant,
        title,
      };
    });

    return sizeOnlyVariants || null;
  },
  isPreLaunch: (state) => {
    return state.product?.tags?.includes("pre-launch") || false;
  },
  hidePreorder: (state) => {
    return state.product?.tags?.includes("hide-preorder") || false;
  },
  preOrder: (state) => {
    const preorder = state.selectedVariant?.randbPreorder?.value;
    if (!preorder) return false;

    return JSON.parse(preorder);
  },
  sellingPlanAllocation: (state, getters) => {
    const sellingPlanAllocations =
      unwrapNodes(state.selectedVariant?.sellingPlanAllocations?.edges) || [];

    const sellingPlanAllocation = sellingPlanAllocations.find((allocation) => {
      return allocation.sellingPlan?.id === getters.preOrder.sellingPlanId;
    });

    return sellingPlanAllocation;
  },
  availableForPreOrder: (state, getters) => {
    if (getters.hidePreorder) return false;
    if (!getters.preOrder) return false;

    const { enabled = false, limit = 0, reserved = 0 } = getters.preOrder;
    const inventory = state.selectedVariant.quantityAvailable || 0;

    return enabled && inventory <= 0 && reserved < limit;
  },
  // Prebuy system getters
  prebuyCampaigns: (state) => {
    if (!state.selectedVariant?.prebuyCampaigns?.value) return null;
    try {
      return JSON.parse(state.selectedVariant.prebuyCampaigns.value);
    } catch (e) {
      console.error("Failed to parse prebuyCampaigns:", e);
      return null;
    }
  },
  selectedPrebuyCampaign: (state, getters) => {
    const campaigns = getters.prebuyCampaigns;
    const inventory = state.selectedVariant?.quantityAvailable || 0;

    // Only show preorders if variant has positive inventory
    if (!campaigns || inventory <= 0) {
      return null;
    }

    // The campaign is pre-resolved per market, keyed by market ID
    // (set on window.shopifyMarket.id by the Liquid template)
    const currentMarketId = window.shopifyMarket?.id;
    if (!currentMarketId) {
      return null;
    }

    return campaigns[String(currentMarketId)] || null;
  },
  prebuySellingPlanAllocation: (state, getters) => {
    const campaign = getters.selectedPrebuyCampaign;
    if (!campaign?.sellingPlanId) return null;

    const sellingPlanAllocations =
      unwrapNodes(state.selectedVariant?.sellingPlanAllocations?.edges) || [];

    // Extract the numeric ID from the sellingPlanId (format: "gid://shopify/SellingPlan/123")
    const campaignPlanId = campaign.sellingPlanId.split("/").pop();

    return sellingPlanAllocations.find((allocation) => {
      const allocationPlanId = allocation.sellingPlan?.id.split("/").pop();
      return allocationPlanId === campaignPlanId;
    });
  },
  availableForPrebuy: (state, getters) => {
    if (getters.hidePreorder) return false;

    const campaign = getters.selectedPrebuyCampaign;
    const inventory = state.selectedVariant?.quantityAvailable || 0;

    return !!campaign && inventory > 0;
  },
  selectedVariantSellingPlanId: (state, getters) => {
    if (getters.hidePreorder || !state.selectedVariant) return null;

    if (
      getters.availableForPrebuy &&
      getters.selectedPrebuyCampaign?.sellingPlanId
    ) {
      return getters.selectedPrebuyCampaign.sellingPlanId;
    }

    if (getters.availableForPreOrder && getters.preOrder?.sellingPlanId) {
      return getters.preOrder.sellingPlanId;
    }

    return null;
  },
  isSelectedVariantPreorder: (state, getters) => {
    if (!getters.selectedVariantSellingPlanId) return false;

    if (getters.availableForPrebuy) {
      return !!getters.prebuySellingPlanAllocation;
    }

    if (getters.availableForPreOrder) {
      return !!getters.sellingPlanAllocation;
    }

    return false;
  },
  isFinalSaleProduct: (state) => {
    const { finalSaleEnabled = false } = getFeatureToggles();
    if (!finalSaleEnabled) return false;

    return hasFinalSaleTag(state.product?.tags);
  },
  isUkWarehouseProduct: (state) => {
    return hasUkWarehouseTag(state.product?.tags);
  },
};

const mutations = {
  setSelectedOptions(state, value) {
    state.selectedOptions = value;
  },
  setProduct(state, value) {
    state.product = value;
    state.preorderModalShown = false;
    state.openPreorderModal = false;
    state.finalSaleModalShown = false;
    state.openFinalSaleVariantModal = false;
    state.ukWarehouseModalShown = false;
    state.openUkWarehouseVariantModal = false;
  },
  setSelectedVariant(state, value) {
    state.selectedVariant = value;
  },
  setDefaultVariant(state, value) {
    state.defaultVariant = value;
  },
  setOpenSizeGuide(state, value) {
    state.openSizeGuide = value;
  },
  setOpenQuickLook(state, value) {
    state.openQuickLook = value;
  },
  setQuickLookProduct(state, value) {
    state.quickLookProduct = value;
  },
  setQuickLookSelectedVariant(state, value) {
    state.quickLookSelectedVariant = value;
  },
  setOpenPreorderModal(state, value) {
    state.openPreorderModal = value;
  },
  setPreorderModalShown(state, value) {
    state.preorderModalShown = value;
  },
  setOpenFinalSaleVariantModal(state, value) {
    state.openFinalSaleVariantModal = value;
  },
  setFinalSaleModalShown(state, value) {
    state.finalSaleModalShown = value;
  },
  setFinalSaleQuickLookProductId(state, value) {
    state.finalSaleQuickLookProductId = value;
  },
  setOpenUkWarehouseVariantModal(state, value) {
    state.openUkWarehouseVariantModal = value;
  },
  setUkWarehouseModalShown(state, value) {
    state.ukWarehouseModalShown = value;
  },
  setUkWarehouseQuickLookProductId(state, value) {
    state.ukWarehouseQuickLookProductId = value;
  },
};

const actions = {
  changeProductOption({ commit, state, dispatch }, option) {
    const selectedOptions = cloneDeep(state.selectedOptions);
    const index = selectedOptions.findIndex(
      (selectedOption) => selectedOption.id === option.id
    );

    if (index === -1) {
      selectedOptions.push(option);
    } else {
      selectedOptions[index] = option;
    }

    commit("setSelectedOptions", selectedOptions);

    dispatch("updateProductUrl");
  },
  setSelectedOptions({ commit }, options) {
    commit("setSelectedOptions", options);
  },
  updateProductUrl({ state, getters }) {
    const selectedOptions = cloneDeep(state.selectedOptions);
    const url = new URL(window.location);
    selectedOptions.forEach((selectedOption) => {
      const actualOption = getters.productOptions.find((productOption) => {
        return productOption.id === selectedOption.id;
      });

      if (actualOption.values.length > 1) {
        url.searchParams.set(
          selectedOption.name.toLowerCase(),
          selectedOption.value.toLowerCase()
        );
      }
    });

    window.history.replaceState(null, "", url.toString());
  },
  updateProductUrlV2({ state }) {
    const url = new URL(window.location);
    let urlChanged = false;

    if (state.selectedVariant) {
      const id = state.selectedVariant.id.split(
        "gid://shopify/ProductVariant/"
      )[1];
      if (url.searchParams.get("variant") !== id) {
        url.searchParams.set("variant", id);
        urlChanged = true;
      }
    }

    if (urlChanged) {
      window.history.replaceState(null, "", url.toString());
    }
  },
  setProduct({ commit }, product) {
    commit("setProduct", product);
  },

  setSelectedVariant({ commit }, value) {
    commit("setSelectedVariant", value);
  },
  setDefaultVariant({ commit }, value) {
    commit("setDefaultVariant", value);
  },
  setOpenSizeGuide({ commit }, value) {
    commit("setOpenSizeGuide", value);
  },
  setOpenQuickLook({ commit }, value) {
    commit("setOpenQuickLook", value);
    if (!value) {
      commit("setQuickLookProduct", null);
      commit("setQuickLookSelectedVariant", null);
    }
  },
  /**
   * Open Shop the Look without replacing the main PDP product in state.
   * Used by ProductCard "+" on Complete the Set / journal cards while on a PDP.
   */
  openProductQuickLook({ commit, dispatch }, product) {
    if (!product) return;

    commit("setQuickLookProduct", product);

    const variants = unwrapNodes(product.variants?.edges) || [];
    const isPreLaunch = product.tags?.includes("pre-launch");
    dispatch(
      "setQuickLookSelectedVariant",
      variants.length === 1 || isPreLaunch ? variants[0] : null
    );
    commit("setOpenQuickLook", true);
  },
  setQuickLookSelectedVariant({ commit, state }, value) {
    commit("setQuickLookSelectedVariant", value);

    const product = state.quickLookProduct;
    if (!value || !product) return;
    if (variantIsClassicPreorder(value, product)) return;

    const { finalSaleEnabled = false } = getFeatureToggles();
    const showFinalSale = finalSaleEnabled && hasFinalSaleTag(product.tags);
    if (showFinalSale && state.finalSaleQuickLookProductId !== product.id) {
      commit("setFinalSaleQuickLookProductId", product.id);
      commit("setOpenFinalSaleVariantModal", true);
      return;
    }

    if (!hasUkWarehouseTag(product.tags)) return;
    if (state.ukWarehouseQuickLookProductId === product.id) return;

    commit("setUkWarehouseQuickLookProductId", product.id);
    commit("setOpenUkWarehouseVariantModal", true);
  },
  setOpenPreorderModal({ commit }, value) {
    commit("setOpenPreorderModal", value);
  },
  setOpenFinalSaleVariantModal({ commit }, value) {
    commit("setOpenFinalSaleVariantModal", value);
  },
  setOpenUkWarehouseVariantModal({ commit }, value) {
    commit("setOpenUkWarehouseVariantModal", value);
  },
  openPreorderModal({ commit }) {
    commit("setOpenPreorderModal", true);
  },
  selectVariantForPurchase({ commit, dispatch, getters, state }, variant) {
    commit("setSelectedVariant", variant);
    dispatch("updateProductUrlV2");

    if (getters.isSelectedVariantPreorder && !state.preorderModalShown) {
      commit("setPreorderModalShown", true);
      commit("setOpenPreorderModal", true);
    } else if (getters.isFinalSaleProduct && !state.finalSaleModalShown) {
      commit("setFinalSaleModalShown", true);
      commit("setOpenFinalSaleVariantModal", true);
    } else if (getters.isUkWarehouseProduct && !state.ukWarehouseModalShown) {
      commit("setUkWarehouseModalShown", true);
      commit("setOpenUkWarehouseVariantModal", true);
    }
  },
  resetPreorderModalSession({ commit }) {
    commit("setPreorderModalShown", false);
    commit("setOpenPreorderModal", false);
    commit("setFinalSaleModalShown", false);
    commit("setOpenFinalSaleVariantModal", false);
    commit("setUkWarehouseModalShown", false);
    commit("setOpenUkWarehouseVariantModal", false);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
